import React from "react";
import {DataGrid, GridActionsCellItem, GridToolbarContainer} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AppBar,
  Card,
  Chip
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
  Edit as EditIcon,
  ToggleOn,
  ToggleOff,
  CheckCircle,
  Block
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET, PATCH} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import properties from "constants/ApplicationConstants";
import {formatType} from "constants/ApplicationConstants";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        p: 1,
        borderTop: "1px solid #e0e0e0",
        gap: 2,
        flexWrap: "nowrap"
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No records"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1, flexShrink: 0}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={() => onPageChange(0)} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous">
          <IconButton
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next">
          <IconButton
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={onGoToLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {backgroundColor: "#1565c0"},
              "&.Mui-disabled": {backgroundColor: "#e0e0e0", color: "#9e9e9e"}
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function SkillList() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize] = React.useState(20);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const [skillType, setSkillType] = React.useState("");
  const [status, setStatus] = React.useState("");

  const skillTypesMap = {
    1: "technical-primary",
    2: "technical-secondary",
    3: "competency-primary",
    4: "competency-secondary",
    5: "questionnaire"
  };

  const [snackbar, setSnackbar] = React.useState({open: false, message: "", severity: "success"});

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({open: true, message, severity});
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({...prev, open: false}));

  const fetchSkills = async (page = 0, skillType = "", status = "", empId = 0) => {
    setLoading(true);
    try {
      let url = `admin/kra-skills/list?page=${page}&size=${pageSize}&empId=${empId}`;

      if (skillType !== "" && skillType !== null) {
        url += `&skillTypes=${skillType}`;
      }

      if (status !== "" && status !== null) {
        url += `&status=${status}`;
      }

      const response = await GET(url);

      if (response?.status === 200) {
        const data = response?.data?.data?.paginatedData || [];

        const formatted = data.map((item, index) => ({
          id: page * pageSize + index + 1,
          skillId: item.paFormId,
          skillSet: item.skillSet,
          skillTypes: formatType(item.skillTypes),
          status: item?.status
        }));

        setRows(formatted);
        setTotalRows(response.data.data.totalItems || 0);
        setTotalPages(response.data.data.totalPages || 0);
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to load current KRA skills", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (skillId, currentStatus) => {
    if (
      !window.confirm(
        `Are you sure you want to mark this skill as ${currentStatus ? "Inactive" : "Active"}?`
      )
    ) {
      return;
    }
    const newStatus = currentStatus ? 0 : 1;

    try {
      const url = `admin/kra-skills/markActiveInactive?paFormId=${skillId}&status=${newStatus}`;
      const response = await PATCH(url);
      if (response?.status === 200) {
        const backendMessage =
          response?.data?.message ||
          (newStatus === 1 ? "Skill activated successfully" : "Skill deactivated successfully");

        showSnackbar(backendMessage, "success");
        fetchSkills(page, skillType, status);
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to update status", "error");
    }
  };

  React.useEffect(() => {
    fetchSkills();
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchSkills(0, skillType, status);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    fetchSkills(newPage, skillType, status);
  };

  const handleGoToLastPage = () => {
    if (totalPages > 0) {
      handlePageChange(totalPages - 1);
    }
  };

  const columns = [
    {field: "id", headerName: "#", width: 70, align: "center", headerAlign: "center"},
    {field: "skillSet", headerName: "Skill Set", width: 380},
    {
      field: "skillTypes",
      headerName: "Skill Type",
      width: 230,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: params => {
        const isActive = params.value;

        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "error"}
            size="small"
            sx={{
              fontWeight: "500",
              minWidth: "80px",
              color: "#fff",
              fontFamily: properties.fontFamily
            }}
          />
        );
      }
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      width: 120,
      getActions: params => [
        <GridActionsCellItem
          icon={
            <Tooltip title={params.row.status ? "Deactivate" : "Activate"}>
              {params.row.status ? <Block color="error" /> : <CheckCircle color="success" />}
            </Tooltip>
          }
          label={params.row.status ? "Deactivate" : "Activate"}
          onClick={() => toggleStatus(params.row.skillId, params.row.status)}
        />
      ]
    }
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static" color="transparent" elevation={0}>
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                  gap: 2,
                  alignItems: "center"
                }}
              >
                <Typography
                  sx={{
                    gridColumn: {xs: "span 12", sm: "span 3"},
                    color: "text.primary",
                    fontFamily: properties.fontFamily,
                    fontSize: "17px",
                    fontWeight: "bold"
                  }}
                >
                  Skills Management
                </Typography>

                <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 3"}}>
                  <InputLabel>Skill Type</InputLabel>
                  <Select
                    value={skillType}
                    label="Skill Type"
                    onChange={e => setSkillType(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                    sx={{minWidth: 150, height: 43}}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {Object.entries(skillTypesMap).map(([id, value]) => (
                      <MenuItem key={id} value={value}>
                        {value
                          .split("-")
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join("-")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 3"}}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={e => setStatus(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>

                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                  sx={{
                    gridColumn: {xs: "span 12", sm: "span 2"},
                    background: properties.colors.bgcolorbluethem || "#1976d2",
                    height: "40px",
                    "&:hover": {
                      background: properties.colors.bgcolorbluethem
                        ? `${properties.colors.bgcolorbluethem}ee`
                        : "#1565c0"
                    }
                  }}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
      </MDBox>

      <MDBox>
        <Paper>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={totalRows}
            pageSizeOptions={[pageSize]}
            disableRowSelectionOnClick
            sx={{
              height: 600,
              fontFamily: properties.fontFamily,
              fontSize: "14px",
              height: "500px",
              "& .MuiDataGrid-scrollbar": {
                width: "10px !important"
              }
            }}
            slots={{
              pagination: () => (
                <CustomPagination
                  page={page}
                  pageSize={pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
              )
            }}
          />
        </Paper>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
